/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {FC} from 'react';
import Chip, {ChipProps} from '@material-ui/core/Chip';
import {withStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {MaterialCheckIcon} from '@ion/dls/src/icons/MaterialCheckIcon';
import {dlsAlt, dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {IconSize} from '@ion/dls/src/types/types';

export enum ChipVariant {
  DEFAULT,
  GREEN
}

export interface ChipTypes extends ChipProps {
  classes: any;
  disabled: boolean;
  isSelected?: boolean;
  isCustomRoot?: boolean;
  showCustomDeleteIcon?: boolean;
  customDeleteIcon?: FC;
  hoverDefault?: boolean;
  maxWidth?: boolean;
  borderRadius?: string;
  labelColor?: string;
  defaultColor?: string;
  textTransform?: 'none' | 'capitalize' | 'uppercase' | 'lowercase';
  noTextTransform?: boolean;
  backgroundStyles?: object;
  isClickable?: boolean;
  isTopSpacing?: boolean;
  showCheckmark?: boolean;
}

// [DLS PACKAGE TODO]: The below styles can safely be ignored as they are implemented
// as a function (ignored due to limitations of material-ui-verifier AST back referencing)
/* eslint-disable ion-eslint-rules/material-ui-verifier */
const styles = theme => {
  const {
    fontFamily,
    fontWeight,
    fontSize,
    lineHeight,
    textTransform,
    letterSpacing,
    labelColor,
    greenVariantColor
  } = dlsTypography('chips');
  const backgroundStyles = {
    backgroundColor : dlsAlt('components', 'icon', 'selected'),
    '&&'            : {
      '&:focus, &:hover' : {
        backgroundColor : dlsAlt('components', 'icon', 'selected')
      }
    }
  };
  return {
    root : {
      backgroundColor : props => props.colorVariant === ChipVariant.GREEN && greenVariantColor,
      maxWidth        : props => props.maxWidth ? 100 : 'auto',
      borderRadius    : props => props.borderRadius || '16px',
      marginRight     : props => props.isCustomRoot ? 0 : '10px',
      marginLeft      : props => props.isCustomRoot ? '10px' : 0,
      marginTop       : props => props.isTopSpacing ? '5px' : 0,
      whiteSpace      : 'nowrap',
      overflow        : 'hidden',
      textOverflow    : 'ellipsis'
    },
    customRoot : {
      marginLeft  : '10px',
      marginRight : '0 !important'
    },
    label : {
      color         : props => props.labelColor || labelColor,
      fontFamily,
      fontSize,
      lineHeight,
      letterSpacing,
      textTransform : props => props.noTextTransform ? 'none' : props.textTransform || textTransform,
      fontWeight
    },
    selectedLabel : {
      color : props => props.defaultColor || dlsColor('aaBlue')
    },
    selectedRoot : {
      borderColor     : props => props.defaultColor || dlsColor('aaBlue'),
      backgroundColor : props => props?.backgroundStyles?.backgroundColor || backgroundStyles.backgroundColor,
      '&&'            : props => props?.backgroundStyles?.hover || backgroundStyles['&&']
    },
    outlined : {
      backgroundColor : props => props?.backgroundStyles?.backgroundColor || backgroundStyles.backgroundColor,
      '&&'            : props => props?.backgroundStyles?.hover || backgroundStyles['&&']
    },
    clickable : {
      backgroundColor : props => props?.backgroundStyles?.backgroundColor || backgroundStyles.backgroundColor,
      '&&'            : props => props?.backgroundStyles?.hover || backgroundStyles['&&']
    },
    deleteIcon : {
      paddingTop : '0px'
    },
    deletable : {
      '&&' : {
        '&:focus, &:hover' : {
          backgroundColor : props => props.hoverDefault ? 'rgb(206, 206, 206)' : 'transparent'
        }
      }
    },
    deletableDefault : {
      '&&' : {
        '&:focus' : {
          backgroundColor : dlsColor('grey5')
        },
        '&:hover' : {
          backgroundColor : 'rgb(206, 206, 206) !important'
        }
      }
    }
  };
};

/**
 * @Chip
 *
 * Defines the Chip component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 *
 *   <Chip label={}
 *     isSelected={}
 *     onClick={}
 *     onDelete={} />
 *
 * ```
 */
const ChipWrapper = ({
  classes,
  disabled,
  customDeleteIcon,
  borderRadius,
  labelColor,
  defaultColor,
  isCustomRoot,
  backgroundStyles,
  noTextTransform = false,
  textTransform = 'none',
  isSelected = false,
  showCustomDeleteIcon = false,
  hoverDefault = false,
  maxWidth = true,
  isClickable = true,
  colorVariant = ChipVariant.DEFAULT,
  showCheckmark = true,
  ...props
}: ChipTypes) => {
  const {iconMargin} = dlsTypography('chips');
  return (
    <Chip disabled={disabled} classes={isSelected
      ? {
        root       : classnames(classes.root, {[classes.selectedRoot]: isClickable}),
        clickable  : classnames({[classes.clickable]: isClickable}),
        outlined   : classes.outlined,
        label      : classnames(classes.label, {[classes.selectedLabel]: isClickable}),
        deleteIcon : classes.deleteIcon
      }
      : {
        root       : classes.root,
        label      : classes.label,
        deleteIcon : classes.deleteIcon,
        deletable  : classnames({
          [classes.deletable]        : isClickable,
          [classes.deletableDefault] : !isClickable
        })
      }} deleteIcon={showCustomDeleteIcon ? <div style={{marginRight: '8px'}}>{customDeleteIcon}</div>
      : isSelected && showCheckmark
        ? (
          <div style={{margin: iconMargin}}>
            <MaterialCheckIcon color={defaultColor || dlsColor('aaBlue')} size={IconSize.small} />
          </div>) : <></>} {...props} />
  );
};
export default withStyles(styles)(ChipWrapper);
